/**
 * asnLocationDetailsAdapter.ts
 * Converts ASN Location Summary API data into Analytics Hub table contracts.
 *
 * Thin translation layer:
 * - No business logic recalculation
 * - No analytics hub modification
 * - Type-safe conversion only
 */

import {
  PagerColumn,
  GroupedRow,
  SparcCategory,
  KpiDirection,
} from '../../../AnalyticsHub/constants/onePagerConstants';
import {
  AsnGroupedRow,
  AsnMetricColumn,
} from '../constants/asnOnePagerConstants';

/**
 * Convert ASN metric column to Analytics Hub PagerColumn.
 * Preserves precision and semantics, fills required fields with appropriate values.
 */
export const convertAsnMetricToPagerColumn = (
  asnCol: AsnMetricColumn,
): PagerColumn => {
  return {
    id: asnCol.id,
    label: asnCol.label,
    sparc: 'N' as SparcCategory, // ASN metrics are "Navigator" category
    dir: asnCol.dir ?? ('lo' as KpiDirection), // Default: lower is better
    fmt: asnCol.fmt as any, // Preserve original format (pct2, int, num1)
    target: asnCol.target ?? 100, // Neutral target if not specified
    gAgg: inferAggregationType(asnCol.id), // Infer from metric ID
    best: 0, // Neutral value (only used for demo data generation)
    worst: 100, // Neutral value (only used for demo data generation)
  };
};

/**
 * Convert array of ASN metric columns to PagerColumn[].
 */
export const convertAsnMetricsToPagerColumns = (
  asnCols: readonly AsnMetricColumn[],
): PagerColumn[] => {
  return asnCols.map(convertAsnMetricToPagerColumn);
};

/**
 * A Analytics Hub `GroupedRow` augmented with the API's stable row `key` —
 * the table uses this directly as its FlatList row identity instead of
 * deriving a composite key from grain values.
 */
export type AsnDisplayRow = GroupedRow & { key: string };

/**
 * Convert ASN grouped row (already mapped from the API response — see
 * `buildAsnGroupedRowFromApi` in `asnOnePagerConstants.ts`) to Analytics Hub
 * GroupedRow. Direct mapping - structures are already compatible; `key` is
 * carried through so the table can use `row.key` as its row identity.
 */
export const convertAsnRowToGroupedRow = (
  asnRow: AsnGroupedRow,
): AsnDisplayRow => {
  return {
    key: asnRow.key,
    grainVals: asnRow.grainVals,
    vals: asnRow.vals,
    memberCount: asnRow.memberCount,
  };
};

/**
 * Convert array of ASN rows to GroupedRow[].
 */
export const convertAsnRowsToGroupedRows = (
  asnRows: readonly AsnGroupedRow[],
): AsnDisplayRow[] => {
  return asnRows.map(convertAsnRowToGroupedRow);
};


/**
 * Convert ASN grand total row to Analytics Hub GroupedRow.
 */
export const convertAsnGrandTotalToGroupedRow = (
  asnGrandTotal: AsnGroupedRow | null,
): GroupedRow | null => {
  if (!asnGrandTotal) return null;

  return {
    grainVals: {}, // Grand total has no grain dimension values
    vals: asnGrandTotal.vals,
    memberCount: asnGrandTotal.memberCount,
  };
};

/**
 * Infer aggregation type from metric ID.
 * Percentages and derived metrics use average, counts use sum.
 */
const inferAggregationType = (metricId: string): 'sum' | 'avg' => {
  // Percentage-type metrics should use average aggregation
  if (
    metricId.toLowerCase().includes('pct') ||
    metricId === 'avgSkus'
  ) {
    return 'avg';
  }
  // Counts and quantities use sum
  return 'sum';
};
